import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { TitleBar } from "@shopify/app-bridge-react";
import {
  Card,
  Page,
  Badge,
  TextField,
  Select,
  LegacyStack,
  IndexTable,
  EmptyState,
  Layout,
  PageActions,
  Button,
  Text,
  Banner,
  BlockStack,
  InlineGrid,
  Box,
  InlineStack,
  Tooltip,
} from "@shopify/polaris";
import primaryFillImage from "images/primary-fill.svg";
import EmailPdfDialog from "./support/EmailPdfDialog";
import AddVariantDialog from "../common/AddVariantDialog";
import CostAdjustments from "./support/CostAdjustments";
import {
  deletePurchaseOrder,
  getPurchaseOrderHorseVariants,
  removePurchaseOrderHorseVariantLineItem,
  updatePurchaseOrder,
  updatePurchaseOrderSelection,
  getRecommendations,
  removeAllPurchaseOrderHorseVariantLineItem,
  importPurchaseOrderLineItems,
  emailPdf,
  downloadFile,
  useShopifyShop,
  usePurchaseOrderHorseVariantSelectedIds,
} from "../../api_utils/requests";
import DeleteConfirmationDialog from "../common/DeleteConfirmationDialog";
import ForecastingForm from "./support/ForecastingForm";
import {
  formatMoney,
  commaDelimiter,
  makeLineItemsMap,
  assertString,
  assertNumber,
  softAssertNumber,
  extractMessageFromError,
  omit,
  cloneNestedObjects,
} from "../../helper_functions/utils";
import { ImportFile } from "../common/ImportFile";
import { ErrorBanner } from "../common/ErrorBanner";
import { Footer } from "../common/Footer";
import { getTotal, KEYS } from "./support/utils";
import type { EditablePurchaseOrderLineItem, IPurchaseOrderLineItem, Option, PurchaseOrderLineItems, PurchaseOrderUpdatePayload } from "../../api_utils/types";
import {
  PurchaseOrderState,
  type IPurchaseOrder,
  type PurchaseOrderLineItemsQueryParams,
  type PurchaseOrderLineItemUpdatePayload,
  type QueryParams,
  type RecommendationQueryParams,
} from "../../api_utils/types";
import type { NonEmptyArray } from "@shopify/polaris/build/ts/src/types";
import type { IndexTableHeading } from "@shopify/polaris/build/ts/src/components/IndexTable";
import { PURCHASE_ORDER_HELP_PAGE_URL } from "../../constants";
import PurchaseOrderDraftRow from "./support/PurchaseOrderDraftRow";
import PurchaseOrderEditCard from "./PurchaseOrderEditCard";
import type { QueryKey} from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import SaveBar from "../common/SaveBar";
import Comments from "./Comments";
import { useNavigate } from "react-router-dom";
import CopyRecommendationsButton from "./support/CopyRecommendationsButton";

const getPurchaseOrderLineItemsPayload = (
  purchaseOrderLineItems: PurchaseOrderLineItems,
): PurchaseOrderLineItemUpdatePayload[] =>
  Object.values(purchaseOrderLineItems).map(
    (purchaseOrderLineItem: EditablePurchaseOrderLineItem): PurchaseOrderLineItemUpdatePayload => {
      return {
        id: purchaseOrderLineItem.id,
        quantity: purchaseOrderLineItem.quantity,
        cost: softAssertNumber(purchaseOrderLineItem.cost),
      } satisfies PurchaseOrderLineItemUpdatePayload;
    },
  );

const assertDate = (date: Date | string, fallback: Date): Date => (date ? new Date(date) : fallback);

const notValidatePurchaseOrderValue = (quantities: PurchaseOrderLineItems): boolean =>
  Object.keys(quantities).some((key) => !quantities[key].quantity || quantities[key].quantity.toString() === "0");

const sortOptions = [
  { label: "Oldest to Newest", value: "created_at-asc", key: "created_at-asc" },
  { label: "Newest to Oldest", value: "created_at-desc", key: "created_at-desc" },
  { label: "Title A-Z", value: "product_title-asc", key: "product_title-asc" },
  { label: "Title Z-A", value: "product_title-desc", key: "product_title-desc" },
  { label: "SKU A-Z", value: "sku-asc", key: "sku-asc" },
  { label: "SKU Z-A", value: "sku-desc", key: "sku-desc" },
  { label: "Available small to big", value: "currently_available-asc", key: "currently_available-asc" },
  { label: "Available big to small", value: "currently_available-desc", key: "currently_available-desc" },
  { label: "Ordered small to big", value: "ordered_quantity-asc", key: "ordered_quantity-asc" },
  { label: "Ordered big to small", value: "ordered_quantity-desc", key: "ordered_quantity-desc" },
  { label: "Sales rate small to big", value: "sales_rate-asc", key: "sales_rate-asc" },
  { label: "Sales rate big to small", value: "sales_rate-desc", key: "sales_rate-desc" },
  { label: "Recommend small to big", value: "needed-asc", key: "needed-asc" },
  { label: "Recommend big to small", value: "needed-desc", key: "needed-desc" },
  { label: "Newest stockout date", value: "oos_date-asc", key: "oos_date-asc" },
  { label: "Oldest stockout date", value: "oos_date-desc", key: "oos_date-desc" },
  { label: "Cost small to big", value: "cost-asc", key: "cost-asc" },
  { label: "Cost big to small", value: "cost-desc", key: "cost-desc" },
  { label: "Quantity small to big", value: "quantity-asc", key: "quantity-asc" },
  { label: "Quantity big to small", value: "quantity-desc", key: "quantity-desc" },
  { label: "Line total small to big", value: "line_total-asc", key: "line_total-asc" },
  { label: "Line total big to small", value: "line_total-desc", key: "line_total-desc" },
];

const findDiff = (
  currentPolis: IPurchaseOrderLineItem["id"][],
  newPolis: IPurchaseOrderLineItem["id"][],
): {
  new: (IPurchaseOrderLineItem["id"])[];
  removed: (IPurchaseOrderLineItem["id"])[];
  unchanged: (IPurchaseOrderLineItem["id"])[];
} => {
  const diff = {
    new: [],
    removed: [],
    unchanged: [],
  };
  const currentPolisIds = new Set<number>(currentPolis.map(assertNumber));
  const newPolisIds = new Set<number>(newPolis.map(assertNumber));
  newPolisIds.forEach((newPoliId) => {
    if (!currentPolisIds.has(newPoliId)) {
      diff.new.push(newPoliId);
    }
  });
  currentPolisIds.forEach((currentPoliId) => {
    if (!newPolisIds.has(currentPoliId)) {
      diff.removed.push(currentPoliId);
    }
  });
  newPolisIds.forEach((newPoliId) => {
    if (currentPolisIds.has(newPoliId)) {
      diff.unchanged.push(newPoliId);
    }
  });
  return diff;
};

const calculateRecommendedQuantity = (poli: IPurchaseOrderLineItem | EditablePurchaseOrderLineItem): number => {
  let {relevant_purchase_order_quantity} = poli;
  if (relevant_purchase_order_quantity === undefined || relevant_purchase_order_quantity === null) {
    relevant_purchase_order_quantity = poli.ordered_quantity || 0;
  }
  let recommendedQuantity = poli.needed || 0;
  if (poli.min && poli.min > recommendedQuantity && (!poli.minimum_order_quantity || poli.min > poli.minimum_order_quantity)) {
    const minRecommendedQuantity = poli.min - (poli.currently_available || 0) - (relevant_purchase_order_quantity || 0);
    recommendedQuantity = Math.max(recommendedQuantity, minRecommendedQuantity);
  } else if (poli.minimum_order_quantity && poli.minimum_order_quantity > recommendedQuantity) {
    recommendedQuantity = poli.minimum_order_quantity;
  }
  return recommendedQuantity;
};

export default function Index({
  purchaseOrder: initialPurchaseOrder,
  refetch,
  currencies,
}: {
  readonly purchaseOrder: IPurchaseOrder;
  readonly refetch: () => void;
  readonly currencies: string[];
}): React.ReactElement {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    shopify.loading(isLoading);
  }, [isLoading]);

  // State
  const [isDirty, setIsDirty] = useState(false);
  const { data: shopifyShop } = useShopifyShop();
  const { readyForRecommendations } = shopifyShop;

  const [importCSVModalIsOpen, setImportCSVModalIsOpen] = useState(false);
  const [sortedValue, setSortedValue] = useState("created_at-asc");
  const [loadingTable, setLoadingTable] = useState(false);
  const [deletionDialogueActive, setDeletionDialogueActive] = useState(false);
  const [addVariantModalActive, setAddVariantModalActive] = useState(false);
  const [emailPdfDialogActive, setEmailPdfDialogActive] = useState(false);
  const [editVendorOrSupplier, setEditVendorOrSupplier] = useState<boolean>(false);
  const [editDestination, setEditDestination] = useState<boolean>(false);

  const handleEmailPdfModalClose = useCallback(() => {
    setEmailPdfDialogActive(false);
  }, []);
  const openImportCSVModal = useCallback(() => {
    setImportCSVModalIsOpen(true);
  }, []);
  const openDeletionModal = useCallback(() => {
    setDeletionDialogueActive(true);
  }, []);
  const openEmailPdfModal = useCallback(() => {
    setEmailPdfDialogActive(true);
  }, []);
  const openAddVariantModal = useCallback(() => {
    setAddVariantModalActive(true);
  }, []);

  // Purchase Order
  const defaultArrivalDate = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    return date;
  }, []);
  const estimatedArrivalDate = assertDate(initialPurchaseOrder?.arrival_date, defaultArrivalDate);
  const initialPurchaseOrderWithArrivalDate = {
    ...initialPurchaseOrder,
    arrival_date: estimatedArrivalDate,
  };
  const resetPurchaseOrder = useRef<IPurchaseOrder>(initialPurchaseOrderWithArrivalDate);
  const [purchaseOrder, setPurchaseOrder] = useState<IPurchaseOrder>(initialPurchaseOrderWithArrivalDate);
  const [selectedForecastingMethod, setSelectedForecastingMethod] = useState<"sales_rate" | "seasonal">(
    initialPurchaseOrder.forecasting_method || "sales_rate",
  );

  const [selectedSamplingPeriod, setSelectedSamplingPeriod] = useState<number>(
    initialPurchaseOrder.sampling_period || 90,
  );

  // PurchaseOrderLineItems
  const lineItemsMap = makeLineItemsMap<EditablePurchaseOrderLineItem>([]);
  const resetPolis = useRef(lineItemsMap);
  const [purchaseOrderLineItems, setPurchaseOrderLineItems] = useState<PurchaseOrderLineItems>(
    cloneNestedObjects(lineItemsMap),
  );

  // Filters
  const [salesChannelIds, setSalesChannelIds] = useState<number[]>([]);
  const [includeAllOutOfStockVariants, setIncludeAllOutOfStockVariants] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [autoAddMessage, setAutoAddMessage] = useState<string>("");
  const clearAutoAddMessage = useCallback(() => {
    setAutoAddMessage("");
  }, []);

  const query = useMemo<PurchaseOrderLineItemsQueryParams>(
    () => ({
      sampling_period: selectedSamplingPeriod,
      arrival_date: purchaseOrder.arrival_date,
      days_of_inventory: purchaseOrder.days_of_inventory,
      forecasting_method: selectedForecastingMethod,
      by_sort: sortedValue,
      sale_shopify_channel_ids: salesChannelIds,
    }),
    [
      selectedSamplingPeriod,
      purchaseOrder.arrival_date,
      purchaseOrder.days_of_inventory,
      selectedForecastingMethod,
      sortedValue,
      salesChannelIds,
    ],
  );

  // Only run this update on update
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (!isInitialMount.current && !isLoading) {
      // manually send the resize event every time the table is updated to fix the table scrollbars
      const event = new Event("resize");
      window.dispatchEvent(event);
    }
    isInitialMount.current = false;
  }, [isLoading]);

  const handleNewPolis = useCallback(
    (response: IPurchaseOrderLineItem[], overwrite = false): void => {
      const editablePolis = (response || []).map((poli: IPurchaseOrderLineItem) => {
        const recommendedQuantity = calculateRecommendedQuantity(poli);

        return {
          ...poli,
          cost: poli.cost ? new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 4,
          }).format(assertNumber(poli.cost)) : undefined,
          quantity: poli.quantity?.toString(),
          sortIndex: undefined,
          recommended_quantity: recommendedQuantity,
        } satisfies EditablePurchaseOrderLineItem;
      });
      const newPolisMap = makeLineItemsMap<EditablePurchaseOrderLineItem>(editablePolis);

      const polisClone = cloneNestedObjects<PurchaseOrderLineItems>(purchaseOrderLineItems);
      const diff = findDiff(Object.keys(polisClone).map(assertNumber), Object.keys(newPolisMap).map(assertNumber));
      diff.new.forEach((newPoliId) => {
        polisClone[newPoliId] = newPolisMap[newPoliId];
      });
      diff.removed.forEach((removedPoliId) => {
        delete polisClone[removedPoliId];
      });
      diff.unchanged.forEach((unchangedPoliId) => {
        const newPoli = newPolisMap[unchangedPoliId];

        const recommendedQuantity = calculateRecommendedQuantity(newPoli);

        polisClone[unchangedPoliId] = {
          ...polisClone[unchangedPoliId],
          currently_available: newPoli.currently_available,
          oos_date: newPoli.oos_date,
          sales_rate: newPoli.sales_rate,
          sales_quantity: newPoli.sales_quantity,
          needed: newPoli.needed,
          ordered_quantity: newPoli.ordered_quantity,
          relevant_purchase_order_quantity: newPoli.relevant_purchase_order_quantity,
          discarded: newPoli.discarded,
          min: newPoli.min,
          recommended_quantity: recommendedQuantity,
        };

        // Most of the time we don't want to overwrite these if the user has unsaved changes
        // BUT if we're importing a CSV, then we do want to overwrite with the new values from the CSV
        if (overwrite) {
          polisClone[unchangedPoliId].cost = newPoli.cost;
          polisClone[unchangedPoliId].quantity = newPoli.quantity;
        }
      });

      // Use the new sorting
      Object.keys(newPolisMap).forEach((poliId) => {
        polisClone[poliId].sortIndex = newPolisMap[poliId].sortIndex;
      });

      setPurchaseOrderLineItems(polisClone);
      resetPolis.current = cloneNestedObjects<PurchaseOrderLineItems>(polisClone);
    },
    [purchaseOrderLineItems],
  );

  const fetchAndUpdateVariant = useCallback(async (): Promise<void> => {
    setLoadingTable(true);
    try {
      const newPolis = await getPurchaseOrderHorseVariants(purchaseOrder.id, query);
      handleNewPolis(newPolis);
    } catch (err: unknown) {
      Rollbar.error(err, { purchaseOrderId: purchaseOrder.id, query });
      console.error(err);
    } finally {
      setIsLoading(false);
      setLoadingTable(false);
    }
  }, [purchaseOrder.id, handleNewPolis, query]);

  useEffect(() => {
    void fetchAndUpdateVariant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleNoteChange = useCallback(
    (note: string) => {
      setPurchaseOrder({ ...purchaseOrder, note });
      setIsDirty(true);
    },
    [purchaseOrder],
  );

  const removeVariant = useCallback(
    async (lineItemId: number): Promise<void> => {
      setIsLoading(true);
      setPurchaseOrderLineItems((prev) => {
        const newPurchaseOrderLineItems = { ...prev };
        delete newPurchaseOrderLineItems[lineItemId];
        return newPurchaseOrderLineItems;
      });

      try {
        await removePurchaseOrderHorseVariantLineItem(lineItemId)
        resetPolis.current = {...resetPolis.current, [lineItemId]: undefined};
      } catch (err: unknown) {
        Rollbar.error(err, { purchaseOrderId: purchaseOrder.id, lineItemId });
        console.error(err);
        const message = extractMessageFromError(err);
        setErrorMessage(message);
        setPurchaseOrderLineItems(cloneNestedObjects(resetPolis.current));
      } finally {
        setIsLoading(false);
      }
    },
    [purchaseOrder.id],
  );

  const removeAllVariants = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setLoadingTable(true);
    const failsafeLineItemsMap = purchaseOrderLineItems;
    try {
      setPurchaseOrderLineItems({} as PurchaseOrderLineItems);
      await removeAllPurchaseOrderHorseVariantLineItem(purchaseOrder.id);
      resetPolis.current = {} as PurchaseOrderLineItems;
      shopify.toast.show("All variants removed");
    } catch (err: unknown) {
      Rollbar.error(err, { purchaseOrderId: purchaseOrder.id });
      console.error(err);
      const message = extractMessageFromError(err);
      setErrorMessage(message);
      setPurchaseOrderLineItems(failsafeLineItemsMap);
    } finally {
      setIsLoading(false);
      setLoadingTable(false);
    }
  }, [purchaseOrder.id, purchaseOrderLineItems]);

  const summarySaveCallback = useRef<() => Promise<void>>(() => Promise.resolve());
  const summaryDiscardCallback = useRef<() => void>(() => null);
  const setSummaryIsDirty = useCallback((summaryIsDirty: boolean): void => {
    setIsDirty(isDirty || summaryIsDirty);
  }, [isDirty]);

  const handleDiscardAction = (): void => {
    setPurchaseOrder(cloneNestedObjects(resetPurchaseOrder.current));
    setPurchaseOrderLineItems(cloneNestedObjects(resetPolis.current));
    setEditVendorOrSupplier(false);
    setEditDestination(false);
    setIsDirty(false);
    summaryDiscardCallback.current();
  };

  const getPayload = useCallback(
    (): PurchaseOrderUpdatePayload => ({
      ...omit(purchaseOrder, ["id", "destination", "created_at", "updated_at", "supplier"]),
      purchase_order_line_items_attributes: getPurchaseOrderLineItemsPayload(purchaseOrderLineItems),
      forecasting_method: selectedForecastingMethod,
      sampling_period: selectedSamplingPeriod,
      arrival_date: purchaseOrder.arrival_date,
      days_of_inventory: purchaseOrder.days_of_inventory || 90,
    }),
    [purchaseOrder, purchaseOrderLineItems, selectedForecastingMethod, selectedSamplingPeriod],
  );

  const handleMarkAsOrdered = useCallback((): void => {
    if (notValidatePurchaseOrderValue(purchaseOrderLineItems)) {
      setErrorMessage("Quantity must be greater or equal to 1");
      return;
    }
    setIsLoading(true);
    const mPayload = {
      purchase_order: {
        ...getPayload(),
        state: PurchaseOrderState.Ordered,
      },
    };
    updatePurchaseOrder(purchaseOrder.id, mPayload)
      .then(() => {
        shopify.toast.show("Save successful");
        refetch();
      })
      .catch((err: unknown): void => {
        Rollbar.error(err, mPayload);
        console.error(err);
        const message = extractMessageFromError(err);
        setErrorMessage(message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [getPayload, purchaseOrder.id, refetch, purchaseOrderLineItems]);

  const handleSaveDraft = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    const summarySavePromise = summarySaveCallback.current();
    const payload = getPayload();

    const updatePurchaseOrderPromise = updatePurchaseOrder(purchaseOrder.id, {
      purchase_order: payload,
    }).then(() => {
      resetPurchaseOrder.current = purchaseOrder;
      resetPolis.current = cloneNestedObjects(purchaseOrderLineItems);
    });

    try {
      await Promise.all([summarySavePromise, updatePurchaseOrderPromise]);
      shopify.toast.show("Save successful");
      setIsDirty(false);
      setEditVendorOrSupplier(false);
      setEditDestination(false);
      await queryClient.invalidateQueries({ queryKey: ["purchase_orders", purchaseOrder.id] });
    } catch (err: unknown) {
        Rollbar.error(err, { payload });
        console.error(err);

        const message = extractMessageFromError(err);
        setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  }, [getPayload, purchaseOrder, purchaseOrderLineItems, queryClient]);

  const handleAutofill = useCallback(
    ({
      percentIncrease,
      quantityIncrease,
    }: {
      percentIncrease?: number;
      quantityIncrease?: number;
    } = {}): void => {
      setPurchaseOrderLineItems((prevPurchaseOrderLineItems) => {
        return Object.values(prevPurchaseOrderLineItems).reduce<PurchaseOrderLineItems>((acc, lineItem) => {
          let newQuantity = Number(lineItem.needed || "0");
          newQuantity = Math.max(newQuantity, 0);
          console.log(lineItem.sku, lineItem.relevant_purchase_order_quantity)
          if (lineItem.min) {
            const minNeeded = lineItem.min - (lineItem.currently_available || 0) - (lineItem.relevant_purchase_order_quantity || 0);
            newQuantity = Math.max(newQuantity, minNeeded);
          }
          if (percentIncrease) {
            newQuantity = Math.ceil(newQuantity * (1 + percentIncrease / 100));
          }
          if (quantityIncrease) {
            newQuantity = Math.ceil(newQuantity + quantityIncrease);
          }
          if (lineItem.minimum_order_quantity && newQuantity < lineItem.minimum_order_quantity) {
            newQuantity = lineItem.minimum_order_quantity;
          }
          if (lineItem.pack_size) {
            newQuantity = Math.ceil(newQuantity / lineItem.pack_size) * lineItem.pack_size;
          }
          acc[lineItem.id] = { ...lineItem, quantity: newQuantity.toString() };
          return acc;
        }, {});
      });
      setIsDirty(true);
    },
    [setIsDirty, setPurchaseOrderLineItems],
  );

  const copyBackorders = useCallback((): void => {
    setPurchaseOrderLineItems((prevPurchaseOrderLineItems) => {
      return Object.values(prevPurchaseOrderLineItems).reduce<PurchaseOrderLineItems>((acc, lineItem) => {
        const itemQuantity = Number(lineItem.currently_available || "0");
        if (itemQuantity < 0) {
          acc[lineItem.id] = { ...lineItem, quantity: (-itemQuantity).toString() };
        }
        return acc;
      }, {});
    });
    setIsDirty(true);
  }, [setIsDirty, setPurchaseOrderLineItems]);

  const setAllQuantities = useCallback((quantity: number): void => {
    setPurchaseOrderLineItems((prevPurchaseOrderLineItems) => {
      return Object.values(prevPurchaseOrderLineItems).reduce<PurchaseOrderLineItems>((acc, lineItem) => {
        acc[lineItem.id] = { ...lineItem, quantity: quantity.toString() };
        return acc;
      }, {});
    });
    setIsDirty(true);
  }, [setIsDirty, setPurchaseOrderLineItems]);

  const setFillQuantities = useCallback((fillToQuantity: number): void => {
    setPurchaseOrderLineItems((prevPurchaseOrderLineItems) => {
      return Object.values(prevPurchaseOrderLineItems).reduce<PurchaseOrderLineItems>((acc, lineItem) => {
        const deficit = fillToQuantity - (lineItem.currently_available || 0);
        const quantity = Math.max(0, deficit);
        acc[lineItem.id] = { ...lineItem, quantity: quantity.toString() };
        return acc;
      }, {});
    });
    setIsDirty(true);
  }, [setIsDirty, setPurchaseOrderLineItems]);

  const handleFormFieldChange = useCallback(
    (
      key: (typeof KEYS)[keyof typeof KEYS],
      value: "sales_rate" | "seasonal" | number | "Any" | number[] | boolean,
    ): void => {
    switch (key) {
      case KEYS.FORECASTING_METHOD: {
        setSelectedForecastingMethod(value as "sales_rate" | "seasonal");
        break;
      }
      case KEYS.SAMPLING_PERIOD: {
        setSelectedSamplingPeriod(value as number);
        break;
      }
      case KEYS.DAYS_OF_INVENTORY: {
        setPurchaseOrder({ ...purchaseOrder, days_of_inventory: value as number });
        break;
      }
      case KEYS.SALES_CHANNEL_IDS: {
        setSalesChannelIds(value === "Any" ? [] : (value as number[]));
        break;
      }
      case KEYS.INCLUDE_ALL_OUT_OF_STOCK_VARIANTS: {
        setIncludeAllOutOfStockVariants(value as boolean);
        break;
      }
      default:
        throw new Error(`Unknown key: ${key}, value: ${value.toString()}`);
    }
  }, [purchaseOrder, setIncludeAllOutOfStockVariants, setPurchaseOrder, setSalesChannelIds, setSelectedForecastingMethod, setSelectedSamplingPeriod]);

  const [autoAddLoading, setAutoAddLoading] = useState(false);
  const autoAdd = useCallback(
    (queryParams: RecommendationQueryParams) => {
      setAutoAddLoading(true);
      setIsLoading(true);
      setLoadingTable(true);
      getRecommendations(purchaseOrder.id, queryParams)
        .then((lineItems) => {
          if (lineItems.length <= Object.keys(purchaseOrderLineItems).length) {
            setAutoAddMessage(
              "Horse doesn't have any variants to recommend. You could try adjusting the recommendation settings.",
            );
            return;
          }
          const diff = findDiff(Object.keys(purchaseOrderLineItems).map(assertNumber), lineItems.map(({id}) => id));
          if (diff.new.length === 0) {
            setAutoAddMessage(
              "Horse doesn't have any more variants to recommend. You could try adjusting the recommendation settings.",
            );
            return;
          }

          handleNewPolis(lineItems);
        })
        .catch((err: unknown): void => {
          Rollbar.error(err, queryParams);
          console.error(err);
          const message = extractMessageFromError(err);
          setErrorMessage(message);
        })
        .finally(() => {
          setIsLoading(false);
          setLoadingTable(false);
          setAutoAddLoading(false);
        });
    },
    [
      purchaseOrder,
      purchaseOrderLineItems,
      setIsLoading,
      setLoadingTable,
      setAutoAddMessage,
      setErrorMessage,
      handleNewPolis,
    ],
  );

  const emptyState = useMemo(() => (
    <EmptyState
      action={{
        content: "Add variants",
        onAction: openAddVariantModal,
      }}
      heading="No variants added"
      image=""
    >
      <p>You have not added any variants to your purchase order yet.</p>
    </EmptyState>
  ), [openAddVariantModal]);

  const total = useMemo(() => getTotal(purchaseOrderLineItems), [purchaseOrderLineItems]);

  const sortedLineItems = Object.values(purchaseOrderLineItems).sort((a, b) => {
    if (a.sortIndex < b.sortIndex) {
      return -1;
    } else {
      return 1;
    }
  });

  const headings = useMemo(() => [
    { title: "Variant" },
    { title: "Available", tooltipContent: "Currently available at the destination" },
    { title: "Ordered", tooltipContent: "Currently ordered quantity to this destination" },
    { title: selectedForecastingMethod === "seasonal" ? "seasonal" : "Sales rate" },
    { title: "Recommendation" },
    { title: "Stockout date", tooltipContent: "Date when the variant went out of stock" },
    { title: "Cost" },
    { title: "Quantity" },
    { title: "Line total" },
    { title: "" },
  ].filter((item): boolean => item.title !== "seasonal") as NonEmptyArray<IndexTableHeading>, [selectedForecastingMethod]);

  const updateSelection = useCallback(
    async (selectionPayload: object, horseVariantQueryParams: QueryParams) =>
      await updatePurchaseOrderSelection(purchaseOrder.id, selectionPayload, horseVariantQueryParams),
    [purchaseOrder.id],
  );

  const setPoli = useCallback(
    (newPoli: EditablePurchaseOrderLineItem): void => {
      setPurchaseOrderLineItems((prev) => ({ ...prev, [newPoli.id]: newPoli }));
      setIsDirty(true);
    },
    [],
  );

  const handleEmailPdf = useCallback(
    async (id, emailPayload): Promise<void> => {
      await emailPdf(id, emailPayload, query);
    },
    [query],
  );

  const selectHorseVariantIds = useMemo(() => {
    return Object.values(purchaseOrderLineItems).map(({ horse_variant_id }) => horse_variant_id);
  }, [purchaseOrderLineItems]);

  const getQuery = useCallback((): RecommendationQueryParams => {
    const queryParams: RecommendationQueryParams = { minimum_needed: undefined };
    if (selectedSamplingPeriod) {
      queryParams.sampling_period = selectedSamplingPeriod;
    }
    if (selectedForecastingMethod) {
      queryParams.forecasting_method = selectedForecastingMethod;
    }
    if (salesChannelIds) {
      queryParams.sale_shopify_channel_ids = salesChannelIds;
    }
    if (purchaseOrder.arrival_date) {
      queryParams.arrival_date = purchaseOrder.arrival_date;
    }
    if (purchaseOrder.days_of_inventory) {
      queryParams.days_of_inventory = purchaseOrder.days_of_inventory;
    }
    if (includeAllOutOfStockVariants) {
      queryParams.include_all_out_of_stock_variants = includeAllOutOfStockVariants;
    }
    return queryParams;
  }, [purchaseOrder, salesChannelIds, selectedForecastingMethod, selectedSamplingPeriod, includeAllOutOfStockVariants]);

  const handleAutoAdd = useCallback(() => {
    const recommendationQuery = getQuery();
    autoAdd(recommendationQuery);
  }, [autoAdd, getQuery]);

  // const maxWidthStyle = { "--pc-box-max-width": "1228px", display: "flex", flexWrap: "wrap" } as React.CSSProperties;
  const maxWidthStyle = {};

  const navigate = useNavigate();

  const handleExportPdf = () => {
    const purchaseOrderName = purchaseOrder.label || purchaseOrder.id;
    const filename = `Horse Purchase Order ${purchaseOrderName}.pdf`;
    setIsLoading(true);
    downloadFile(`/purchase_orders/${purchaseOrder.id}.pdf`, filename, query)
      .then(() => {
        setIsLoading(false);
      })
      .catch((err: unknown): void => {
        Rollbar.error(err, query);
        console.error(err);
        setIsLoading(false);
        setErrorMessage("Failed to generate PDF");
      });
  };

  const handleExportCsv = async (): Promise<void> => {
    const purchaseOrderName = purchaseOrder.label || purchaseOrder.id;
    const filename = `Horse Purchase Order ${purchaseOrderName}.csv`;
    await downloadFile(`/purchase_orders/${purchaseOrder.id}.csv${window.location.search}`, filename);
  };

  const poliSelectedIdsQueryKey: QueryKey = ["purchase_order_horse_variants_selected_ids", purchaseOrder.id];
  const querySelected = usePurchaseOrderHorseVariantSelectedIds(purchaseOrder.id, {
    enabled: false,
    queryKey: poliSelectedIdsQueryKey,
  });

  return (
    <Page fullWidth titleMetadata={<Badge tone="info">Draft</Badge>}>
      <TitleBar title="Draft purchase order">
        <button
          onClick={async () => {
            await shopify.saveBar.leaveConfirmation();

            navigate("/purchase_orders");
          }}
          type="button"
          variant="breadcrumb"
        >
          Purchase orders
        </button>
        <button onClick={handleMarkAsOrdered} type="button" variant="primary">
          Mark as ordered
        </button>
        <section label="More actions">
          <button onClick={handleExportPdf} type="button">
            Export PDF
          </button>
          <button onClick={openEmailPdfModal} type="button">
            Email PDF
          </button>
          <button onClick={handleExportCsv} type="button">
            Export CSV
          </button>
          <button onClick={openImportCSVModal} type="button">
            Import CSV
          </button>
        </section>
        <button onClick={openAddVariantModal} type="button">
          Add variants
        </button>
      </TitleBar>
      <SaveBar handleDiscard={handleDiscardAction} handleSave={handleSaveDraft} isDirty={isDirty} />
      <EmailPdfDialog
        active={emailPdfDialogActive}
        onClose={handleEmailPdfModalClose}
        onSend={handleEmailPdf}
        purchaseOrder={purchaseOrder}
        setErrorMessage={setErrorMessage}
      />
      <ImportFile
        apiCallMethod={async (formData) => importPurchaseOrderLineItems(purchaseOrder.id, formData)}
        modalIsOpen={importCSVModalIsOpen}
        setApiResponse={(newPOLIs: IPurchaseOrderLineItem[]): void => {
          handleNewPolis(newPOLIs, true);
        }}
        setErrorMessage={setErrorMessage}
        setModalIsOpen={setImportCSVModalIsOpen}
        title="Import purchase order CSV"
      />
      <Layout>
        {errorMessage ? (
          <Layout.Section variant="fullWidth">
            <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
          </Layout.Section>
        ) : null}

        <Layout.Section variant="oneHalf">
          <PurchaseOrderEditCard
            currencies={currencies}
            editDestination={editDestination}
            editVendorOrSupplier={editVendorOrSupplier}
            purchaseOrder={purchaseOrder}
            setEditDestination={setEditDestination}
            setEditVendorOrSupplier={setEditVendorOrSupplier}
            setIsDirty={setIsDirty}
            setPurchaseOrder={setPurchaseOrder}
          />
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="400">
                <img src={primaryFillImage} />
                <Text as="h2" variant="headingSm">
                  Recommendations
                </Text>
              </InlineStack>
              <ForecastingForm
                arrivalDate={purchaseOrder.arrival_date}
                daysOfInventory={purchaseOrder.days_of_inventory}
                handleFormFieldChange={handleFormFieldChange}
                includeAllOutOfStockVariants={includeAllOutOfStockVariants}
                salesChannelIds={salesChannelIds}
                selectedForecastingMethod={selectedForecastingMethod}
                selectedSamplingPeriod={selectedSamplingPeriod}
              />
              {autoAddMessage ? (
                <Box>
                  <Banner onDismiss={clearAutoAddMessage}>
                    <p>{autoAddMessage}</p>
                  </Banner>
                </Box>
              ) : null}
              <InlineStack align="center">
                {readyForRecommendations ? (
                  <Button disabled={autoAddLoading} onClick={handleAutoAdd} variant="primary">
                    Auto-add variants
                  </Button>
                ) : (
                  <Tooltip content="Please wait while we finish importing your store">
                    <span>
                      <Button disabled onClick={handleAutoAdd} variant="primary">
                        Auto-add variants
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
        <Layout.Section variant="fullWidth">
          <Card padding="0">
            <BlockStack gap="400">
              <BlockStack gap="200">
                <Box padding="400" paddingBlockEnd="0">
                  <InlineStack align="center">
                    <AddVariantDialog
                      active={addVariantModalActive}
                      handleVariantUpdate={fetchAndUpdateVariant}
                      initialHorseLocationId={purchaseOrder.horse_location_id}
                      initialSupplierId={purchaseOrder.supplier?.id}
                      initialVendor={purchaseOrder.vendor}
                      queryKey={poliSelectedIdsQueryKey}
                      querySelected={querySelected}
                      removeAllVariants={removeAllVariants}
                      selectionKey="purchase_order_line_items_attributes"
                      setActive={setAddVariantModalActive}
                      setErrorMessage={setErrorMessage}
                      updateSelection={updateSelection}
                    />
                  </InlineStack>
                </Box>
                <Box paddingInlineEnd="400">
                  <LegacyStack alignment="center" distribution="trailing">
                    <CopyRecommendationsButton
                      copyBackorders={copyBackorders}
                      disabled={sortedLineItems.length === 0}
                      handleAutofill={handleAutofill}
                      setAllQuantities={setAllQuantities}
                      setFillQuantities={setFillQuantities}
                    />
                    <Select
                      disabled={isDirty}
                      label="Sort by"
                      labelInline
                      onChange={setSortedValue}
                      options={sortOptions satisfies Option[]}
                      value={sortedValue}
                    />
                  </LegacyStack>
                </Box>
              </BlockStack>
              <IndexTable
                emptyState={emptyState}
                headings={headings}
                itemCount={sortedLineItems.length}
                loading={loadingTable}
                selectable={false}
              >
                <IndexTable.Row id="0" key={0} position={0} rowType="subheader">
                  <IndexTable.Cell>Totals</IndexTable.Cell>
                  <IndexTable.Cell />
                  <IndexTable.Cell />
                  {selectedForecastingMethod === "sales_rate" && <IndexTable.Cell />}
                  <IndexTable.Cell />
                  <IndexTable.Cell />
                  <IndexTable.Cell />
                  <IndexTable.Cell>{commaDelimiter(total.quantity)}</IndexTable.Cell>
                  <IndexTable.Cell>{formatMoney(total.totalCost, purchaseOrder.currency)}</IndexTable.Cell>
                  <IndexTable.Cell />
                </IndexTable.Row>
                {sortedLineItems.map((poli: EditablePurchaseOrderLineItem, index) => (
                  <PurchaseOrderDraftRow
                    currency={purchaseOrder.currency}
                    key={`purchase-order-draft-row:${poli.id}`}
                    poli={poli}
                    position={index + 1}
                    purchaseOrder={purchaseOrder}
                    readyForRecommendations={readyForRecommendations}
                    removeVariant={removeVariant}
                    selectedForecastingMethod={selectedForecastingMethod}
                    setPoli={setPoli}
                  />
                ))}
              </IndexTable>
            </BlockStack>
          </Card>
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <Card>
            <InlineGrid columns="1fr auto">
              <Text as="h2" variant="headingSm">
                Note to supplier
              </Text>
            </InlineGrid>
            <Box>
              <TextField
                autoComplete="off"
                // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                error={purchaseOrder.note?.length >= 1000 ? "Note is too long" : ""}
                label="Note"
                maxLength={1000}
                multiline={2}
                onChange={handleNoteChange}
                value={assertString(purchaseOrder.note)}
              />
            </Box>
          </Card>
        </Layout.Section>
        <Layout.Section variant="oneHalf">
          <Card>
            <InlineGrid columns="1fr auto">
              <Text as="h2" variant="headingSm">
                Summary
              </Text>
            </InlineGrid>
            <Box>
              <CostAdjustments
                currency={purchaseOrder.currency}
                discardCallback={summaryDiscardCallback}
                lineTotal={Number(total.totalCost)}
                purchaseOrder={purchaseOrder}
                saveCallback={summarySaveCallback}
                setErrorMessage={setErrorMessage}
                setIsDirty={setSummaryIsDirty}
              />
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
      <PageActions
        secondaryActions={[
          {
            content: "Delete",
            destructive: true,
            onAction: openDeletionModal,
          },
        ]}
      />
      <DeleteConfirmationDialog
        active={deletionDialogueActive}
        content="Are you sure you want to delete this purchase order? This action cannot be reversed."
        deleteItem={deletePurchaseOrder}
        gotoPath="/purchase_orders"
        itemId={purchaseOrder.id}
        setActive={setDeletionDialogueActive}
        title="Delete purchase"
        toastMessage="Purchase order deleted"
      />
      <div style={{ maxWidth: "640px", margin: "0 auto" }}>
        <Comments />
      </div>
      <Footer pageTitle="purchase orders" url={PURCHASE_ORDER_HELP_PAGE_URL} />
    </Page>
  );
}
